import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Undo2, Ruler, PenLine, Check } from "lucide-react";

function posLabel(x, y, w, h) {
  const cx = x + w / 2, cy = y + h / 2;
  const hor = cx < 0.34 ? "stânga" : cx > 0.66 ? "dreapta" : "centru";
  const ver = cy < 0.34 ? "sus" : cy > 0.66 ? "jos" : "mijloc";
  return `${ver}-${hor}`;
}

export function ZoneDrawer({ src, onChange, onCalibrate }) {
  const ref = useRef(null);
  const [boxes, setBoxes] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [mode, setMode] = useState("zone"); // 'zone' | 'calibrate'
  const [refBox, setRefBox] = useState(null);
  const [refMeters, setRefMeters] = useState("2");
  const [scale, setScale] = useState(null);

  const rel = (e) => {
    const r = ref.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: Math.min(1, Math.max(0, (p.clientX - r.left) / r.width)),
             y: Math.min(1, Math.max(0, (p.clientY - r.top) / r.height)) };
  };

  const start = (e) => { const p = rel(e); setDrawing({ x: p.x, y: p.y, w: 0, h: 0 }); };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = rel(e);
    setDrawing((d) => ({ x: Math.min(d.x, p.x), y: Math.min(d.y, p.y), w: Math.abs(p.x - d.x), h: Math.abs(p.y - d.y) }));
  };
  const end = () => {
    if (!drawing) return;
    if (mode === "calibrate") {
      if (drawing.w > 0.02 || drawing.h > 0.02) setRefBox(drawing);
      setDrawing(null);
      return;
    }
    if (drawing.w > 0.03 && drawing.h > 0.03) {
      const next = [...boxes, drawing];
      setBoxes(next);
      emit(next);
    }
    setDrawing(null);
  };

  const emit = (arr) => onChange?.(arr.map((b, i) => ({
    label: `Zonă ${i + 1} (${posLabel(b.x, b.y, b.w, b.h)})`,
    x: b.x, y: b.y, w: b.w, h: b.h,
  })));
  const undo = () => { const n = boxes.slice(0, -1); setBoxes(n); emit(n); };
  const clear = () => { setBoxes([]); emit([]); };

  const applyCalibration = () => {
    if (!refBox) return;
    const m = parseFloat(String(refMeters).replace(",", "."));
    const refNorm = Math.max(refBox.w, refBox.h);
    if (!m || m <= 0 || refNorm <= 0) return;
    const s = m / refNorm; // meters per normalized unit
    setScale(s);
    onCalibrate?.(s);
    setMode("zone");
  };
  const clearCalibration = () => { setRefBox(null); setScale(null); onCalibrate?.(null); };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <button type="button" onClick={() => setMode("zone")} data-testid="mode-zone"
          className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 transition-colors ${mode === "zone" ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400"}`}>
          <PenLine className="w-3.5 h-3.5" /> Desenează zone
        </button>
        <button type="button" onClick={() => setMode("calibrate")} data-testid="mode-calibrate"
          className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 transition-colors ${mode === "calibrate" ? "border-accent bg-accent/15 text-white" : "border-slate-700 text-slate-400"}`}>
          <Ruler className="w-3.5 h-3.5" /> Calibrează scara
        </button>
        {scale && (
          <span className="text-[10px] text-green-400 flex items-center gap-1" data-testid="calibrated-badge">
            <Check className="w-3 h-3" /> Scară calibrată
          </span>
        )}
      </div>

      <div
        ref={ref}
        className="relative w-full rounded-2xl overflow-hidden border border-slate-800 select-none touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="zone-drawer-canvas"
      >
        <img src={src} alt="" className="w-full block pointer-events-none" draggable={false} />
        {boxes.map((b, i) => (
          <div key={i} className="absolute border-2 border-primary bg-primary/20"
            style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%` }}>
            <span className="absolute -top-5 left-0 text-[10px] bg-primary text-white px-1.5 rounded">Zonă {i + 1}</span>
          </div>
        ))}
        {refBox && (
          <div className="absolute border-2 border-amber-400 bg-amber-400/20" data-testid="ref-box"
            style={{ left: `${refBox.x * 100}%`, top: `${refBox.y * 100}%`, width: `${refBox.w * 100}%`, height: `${refBox.h * 100}%` }}>
            <span className="absolute -top-5 left-0 text-[10px] bg-amber-400 text-black px-1.5 rounded flex items-center gap-0.5">
              <Ruler className="w-2.5 h-2.5" /> Referință
            </span>
          </div>
        )}
        {drawing && (
          <div className={`absolute border-2 ${mode === "calibrate" ? "border-amber-400 bg-amber-400/20" : "border-accent bg-accent/20"}`}
            style={{ left: `${drawing.x * 100}%`, top: `${drawing.y * 100}%`, width: `${drawing.w * 100}%`, height: `${drawing.h * 100}%` }} />
        )}
      </div>

      {mode === "calibrate" && (
        <div className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3" data-testid="calibrate-panel">
          <p className="text-[11px] text-slate-300 mb-2">
            Desenează peste un obiect cu lungime cunoscută (ex: o ușă de ~2 m), apoi introdu lungimea reală pentru a calibra scara.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="number" min="0.1" step="0.1" value={refMeters} onChange={(e) => setRefMeters(e.target.value)}
              data-testid="ref-meters-input" className="h-9 w-24 bg-slate-900 border-slate-700" />
            <span className="text-xs text-slate-400">metri</span>
            <Button type="button" size="sm" onClick={applyCalibration} disabled={!refBox}
              data-testid="apply-calibration" className="h-9 bg-amber-500 hover:bg-amber-500/90 text-black font-medium">
              <Check className="w-4 h-4 mr-1" /> Aplică scara
            </Button>
            {scale && (
              <Button type="button" size="sm" variant="outline" onClick={clearCalibration}
                data-testid="clear-calibration" className="h-9 border-slate-700 text-slate-300">Resetează</Button>
            )}
          </div>
          {!refBox && <p className="text-[11px] text-amber-400/80 mt-2">Desenează întâi obiectul de referință pe poză.</p>}
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] text-slate-500">
          {mode === "calibrate" ? "Mod calibrare: desenează obiectul de referință." : "Desenează dreptunghiuri peste zonele de iluminat."}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={undo} disabled={!boxes.length}
            data-testid="zone-undo" className="h-8 border-slate-700 text-slate-300"><Undo2 className="w-3.5 h-3.5" /></Button>
          <Button type="button" size="sm" variant="outline" onClick={clear} disabled={!boxes.length}
            data-testid="zone-clear" className="h-8 border-slate-700 text-slate-300"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}
